import { useEffect, useState, useRef, useMemo } from "react"
import "./App.css"
import { Navigate, Route, Routes, useNavigate } from "react-router-dom"
import { getRandomId, getUserCreds, host, socket } from "./utils/utils"
import Swal from "sweetalert2"
import messages from "./utils/messages"
import PrivateRoute from "./Components/PrivateRoute"
import errors from "./utils/errors"
import {
  drawCard,
  drawAndKeepCard,
  getPlayerOrderFromPlayerId,
  runAuctionWonAnimation,
  runGoToJailAnimation,
  startPieceMovementAnimation,
  startPieceMovementAnimationWithoutJumping,
  playActionCard,
  getInBetweenCellsIdsForMovement,
  runTradedCardsFlipAnimation,
} from "./utils/animationHelpers"
import { runStrikingBeamAnimation } from "./utils/strikingBeamAnimation"
import { prepareSwapPositionsAnimation } from "./utils/swapPositionAnimation"
import { mergeSituationUpdate } from "./utils/situationSync"
import { setActiveGameForAnimations } from "./utils/animationSpeed"
import { setGameTimers } from "./utils/gameTimers"
import {
  getAnimationMultiplier,
  getAnimationTimings,
  featureFlags,
  tradeAcceptTime,
} from "@mono/constants"
import { bankOrder, mapDesigns } from "@mono/mapDesigns"
import {
  audioManager,
  playAuctionWonSound,
  playLightTap2Sound,
  playLightTapSound,
  playPropertySoldSound,
  playSmallErrorSound,
  playTradeOfferedSound,
  playTurnStartedSound,
} from "./utils/audioManager"

import "rc-select/assets/index.css"
import "./index.css"
import "./react-env.d.ts"

import { getCurrentPlayer } from "./utils/gameHelpers"
import { getGameTimings } from "@mono/constants"
import classNames from "classnames"

// Import major components directly
import LandingPage from "./Pages/LandingPage"
import RegisterPage from "./Pages/Register"
import LoginPage from "./Pages/Login"
import LobbyPage from "./Pages/Lobby"
import ForgotPasswordPage from "./Pages/ForgotPassword"
import ForgotPasswordOtpPage from "./Pages/ForgotPassOpt"
import SetNewPasswordPage from "./Pages/SetNewPassword"
import NewGamePage from "./Pages/NewGame"
import GamePage from "./Pages/Game"
import BuyOrAuctionModalPage from "./Modals/BuyOrAuctionModal"
import JailModalPage from "./Modals/JailModal"
import TradePage from "./Pages/Trade"
import AuctionPage from "./Pages/Auction"
import PropertyManagementPage from "./Pages/PropertyManagement"
import JailFreeCardModalPage from "./Modals/JailFreeCardModal"
import AlreadyInGamePage from "./Pages/AlreadyInGame"
import PlayerCardsModalPage from "./Modals/PlayerCardsModal"
import DoubleRentModalPage from "@/Modals/DoubleRentModal"
import SayNoModalPage from "./Modals/SayNoModal"
import ForcedBuyModalPage from "./Modals/ForcedBuyModal"
import GameEndModalWithoutStats from "./Modals/GameEndModalWithoutStats"
import StockModalPage from "./Modals/StockModal"
import RolePickingModalPage from "./Modals/RolePickingModal"
import GalleryPage from "./Pages/Gallery"
import RulesPage from "./Pages/Rules"
import AuctionResult from "./Modals/AuctionResult"
import HiddenAuctionModal from "./Modals/HiddenAuctionModal"
import newTurnAudioSrc from "@/audio/turn-started.mp3"
// LandingPage2 removed — "/" now redirects to /lobby or /login
import DecisionHubModalPage from "./Modals/DecisionHubModal"
import ProfileSettings from "./Pages/ProfileSettings"
import Stats from "./Pages/Stats"
import GameHistory from "./Pages/History/GameHistory"
import GameHistoryDetail from "./Pages/History/GameHistoryDetail"
import TradeSimulator from "./Pages/TradeSimulator"
import Friends from "./Pages/Friends"
import ChallengesList from "./Pages/ChallengesList"
import FullStats from "./Components/FullStats"
import ToastContainer from "./Components/ToastContainer"
import LandscapeOverlay from "./Components/LandscapeOverlay"
import HowToPlay from "./Pages/HowToPlay"
import PrivacyPolicy from "./Pages/PrivacyPolicy"
import DeleteAccount from "./Pages/DeleteAccount"
import { useBackgroundMusic } from "./Hooks/useBackgroundMusic"
import { useFullscreenHotkey } from "./Hooks/useFullscreen"
import { useGameButtonClickSound } from "./Hooks/useGameButtonClickSound"
import { images, allPieces } from "./utils/data"
import { getAllAssets } from "./utils/assets"
import { useAppDispatch, useAppSelector } from "./store/hooks"
import {
  setState,
  setGameSituation,
  setModalData,
  setAnimationData,
  addGlowingProperties,
  addTradeGlowProperties,
  addNotification,
  removeNotification,
  addToastNotification,
  setTradeData,
  resetAllModals,
  incementActionCardAnimationRunning,
  decrementActionCardAnimationRunning,
  setOrientation,
  setIsMobile,
  addChatMessage,
  showChatCloud,
  hideChatCloud,
  resetTradeData,
  rememberDeclinedTrade,
  forgetDeclinedTrade,
  resetStateOnGameStart,
  addMoneyAnimation,
  addMoneyAnimations,
  setIdleWarning,
  setBotTakeoverCountdown,
  setPreferredPieceId,
  setGameSettings,
} from "./store/globalSlice"
import { setCachedPreferredPieceId } from "./utils/piecePreference"

const newTurnAudio = new Audio(newTurnAudioSrc)

// Cards that act on every other player at once — the striking beam fans out to
// all of them instead of a single target.
const allPlayersBeamCards: ActionCardId[] = ["get-10-percent-from-all-players"]

function App() {
  const dispatch = useAppDispatch()
  useGameButtonClickSound()
  useBackgroundMusic()
  // F11 anywhere in the app toggles the game's own fullscreen, so it never
  // drifts apart from the one the toggle button controls.
  useFullscreenHotkey()

  // Use specific selectors to avoid unnecessary re-renders
  const cheats = useAppSelector((state) => state.global.cheats)
  const isLoggedIn = useAppSelector((state) => state.global.isLoggedIn)
  const myGame = useAppSelector((state) => state.global.myGame)
  const myGameId = useAppSelector((state) => state.global.myGame?.id)
  const myGameTurn = useAppSelector(
    (state) => state.global.myGame?.situation?.turn
  )
  const isMyTurn = useMemo(() => {
    return myGameTurn === getUserCreds().email
  }, [myGameTurn, getUserCreds().email])

  const myPlayerOrder = useMemo(() => {
    return myGame?.situation?.players.find(
      (player) => player.id === getUserCreds().email
    )?.order as PlayerOrder
  }, [myGame, getUserCreds().email])

  const myGameSituation = useAppSelector(
    (state) => state.global.myGame?.situation
  )
  const myGameSituationEvents = useAppSelector(
    (state) => state.global.myGame?.situation?.events
  )
  const modalData = useAppSelector((state) => state.global.modalData)
  const elements = useAppSelector((state) => state.global.elements)
  const animationData = useAppSelector((state) => state.global.animationData)
  const declaredBankruptcy = useAppSelector(
    (state) => state.global.declaredBankruptcy
  )
  const tempTradeData = useAppSelector((state) => state.global.tempTradeData)
  const actionCardAnimationRunning = useAppSelector(
    (state) => state.global.actionCardAnimationRunning
  )
  const userData = useAppSelector((state) => state.global.userData)

  // Create a ref to access current state values in socket listeners
  const stateRef = useRef({
    myGame,
    elements,
    animationData,
    modalData,
    actionCardAnimationRunning,
    userData,
  })

  useEffect(() => {
    stateRef.current = {
      myGame,
      elements,
      animationData,
      modalData,
      actionCardAnimationRunning,
      userData,
    }
    // The pure animation helpers have no game to read the speed off, so hand
    // them the one on screen — a quick game runs them shorter, and the server
    // waits on the same numbers.
    setActiveGameForAnimations(myGame)
  }, [
    myGame,
    elements,
    animationData,
    modalData,
    actionCardAnimationRunning,
    userData,
  ])

  // Set when a "swap position with any player" card is played; the next
  // situation update showing the pair's positions exchanged plays the
  // crossing-arcs swap animation instead of letting the pieces teleport.
  const pendingSwapAnimRef = useRef<{
    a: PlayerOrder
    b: PlayerOrder
    expiresAt: number
  } | null>(null)

  // Throttles resync requests. A gap in an append-only list usually shows up on
  // several consecutive updates before the resync lands, and one request per
  // update would have every affected client hammering the server at once.
  const lastResyncRequestRef = useRef(0)
  const requestSituationResync = () => {
    const roomid = stateRef.current.myGame?.id
    if (!roomid) return
    const now = Date.now()
    if (now - lastResyncRequestRef.current < 2000) return
    lastResyncRequestRef.current = now
    socket.emit("game.requestSituationSync", roomid)
  }

  const [testChanceId, setTestChanceId] = useState<ChanceId>("")
  const [testCommunityChestId, setTestCommunityChestId] =
    useState<EventCardId>("")
  const [imagesLoaded, setImagesLoaded] = useState(false)

  const [count, setCount] = useState(0)
  const nav = useNavigate()

  useEffect(() => {
    const loadAllAssets = async () => {
      try {
        console.time("loadAssets")
        const assets = await getAllAssets()

        // Preload all images and audio in parallel
        await Promise.all([
          // Preload all images to ensure they're cached
          Promise.all(
            assets.map((url) => {
              return new Promise((resolve, reject) => {
                const img = new Image()
                img.onload = () => resolve(url)
                img.onerror = () =>
                  reject(new Error(`Failed to load image: ${url}`))
                //@ts-ignore
                img.src = url
              })
            })
          ),
          // Preload all audio buffers (non-blocking - will continue even if audio fails)
          audioManager.preloadAll().catch((error) => {
            console.warn(
              "Audio preloading failed (will retry on first play):",
              error
            )
          }),
        ])

        console.timeEnd("loadAssets")
        setImagesLoaded(true)
      } catch (error) {
        console.error("Error loading assets:", error)
        // Set to true anyway to prevent infinite loading
        setImagesLoaded(true)
      }
    }

    loadAllAssets()
  }, [])
  // Initialize audio context on first user interaction (required for iOS/Safari)
  useEffect(() => {
    const initializeAudioOnInteraction = async () => {
      try {
        await audioManager.preloadAll()
      } catch (error) {
        console.warn("Audio initialization on interaction failed:", error)
      }
    }

    // Try to initialize on any user interaction
    const events = ["pointerdown", "click", "touchstart", "keydown"]
    const handlers = events.map((event) => {
      const handler = () => {
        initializeAudioOnInteraction()
        events.forEach((e) => {
          document.removeEventListener(e, handler)
        })
      }
      document.addEventListener(event, handler, { once: true })
      return handler
    })

    return () => {
      events.forEach((event, index) => {
        document.removeEventListener(event, handlers[index])
      })
    }
  }, [])

  // Account data, fetched on boot and again after a login in this same tab —
  // otherwise someone who logs in without reloading keeps the previous user's
  // details, and their own game piece would only appear after a refresh.
  useEffect(() => {
    window.scrollTo(0, 1)
    if (!getUserCreds().email) return

    fetch(`${host}/api/user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: getUserCreds().email,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        // A 404 answers with { error: "User not found" }, which is not an
        // account. Storing that as the account left every screen reading
        // `userData` with no name and the placeholder avatar, with nothing to
        // ever replace it.
        if (!data || data.error) {
          console.error("Failed to load user data", data?.error)
          return
        }
        dispatch(setState({ userData: data }))
        // The account owns the piece choice, so what comes back here wins over
        // the cached value the store started with — including "no choice", which
        // is why this runs even when the field is missing. A failed request (a
        // guest, or no connection) leaves the cached value alone instead.
        const email = getUserCreds().email
        const preferredPieceId = allPieces.some(
          (piece) => piece.id === data.preferredPieceId
        )
          ? (data.preferredPieceId as string)
          : undefined
        setCachedPreferredPieceId(email, preferredPieceId)
        dispatch(setPreferredPieceId(preferredPieceId))
      })
      .catch((err) => {
        console.error("Failed to load user data", err)
      })
  }, [isLoggedIn])
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "o") {
        e.preventDefault()
        const newCheatsValue = !cheats
        localStorage.setItem("cheats", newCheatsValue.toString())
        dispatch(setState({ cheats: newCheatsValue }))
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [cheats, dispatch])
  useEffect(() => {
    // Off by handler reference so any other "connect" subscriber is untouched.
    const handleConnect = () => {
      socket.emit("verifyToken", getUserCreds().token, (res) => {
        if (!res) {
          nav("/login")
        } else socket.emit("game.visited-game-link", myGameId || "")
      })
    }
    socket.on("connect", handleConnect)
    return () => {
      socket.off("connect", handleConnect)
    }
  }, [myGameTurn, myGameId, nav])
  // useEffect(() => {
  //   if (isMyTurn && !modalData.rolePickingModal?.visible) {
  //     newTurnAudio.play()
  //     dispatch(
  //       addNotification({
  //         type: "turn-started",
  //         duration: 2000,
  //         playerOrder: myPlayerOrder,
  //       })
  //     )
  //   }
  // }, [isMyTurn, modalData.rolePickingModal?.visible, myPlayerOrder])

  useEffect(() => {
    // Registers a listener that only fires while we are actually in a game.
    //
    // Every handler in this effect lives in App, above <Routes>, and so does
    // the toast container and the end-game modal — being on the lobby screen
    // stops none of them. Leaving a game clears `myGame` (see the leftGame
    // reducer) and the server drops the socket from the room in the same
    // breath, so this is really about the events already in flight: without the
    // guard they still land, and the player who just walked out gets the
    // table's turn sounds, its "player left" toasts and its winner modal on top
    // of the lobby.
    //
    // Only game-scoped events go through here. Lobby, auth and invite events —
    // and "game.updateGlobalState", which is how the server hands `myGame` back
    // after a refresh — are registered directly, because they have to work when
    // there is no game.
    const onGameEvent = <E extends keyof ServerToClientEvents>(
      event: E,
      handler: ServerToClientEvents[E]
    ) => {
      // socket.io's own listener type does not survive a generic event name,
      // so the registration is cast. Callers keep full inference: `event` fixes
      // E, and E types `handler`'s arguments.
      socket.on(event as any, ((...args: any[]) => {
        if (!stateRef.current.myGame) return
        ;(handler as (...handlerArgs: any[]) => void)(...args)
      }) as any)
    }

    socket.on("error", ({ text, title }) => {
      Swal.fire({
        title: title || "Error",
        text,
        icon: "error",
        position: "center",
        heightAuto: false,
      })
    })
    socket.on("message", ({ text, title }) => {
      Swal.fire({
        title: title,
        text,
        position: "center",
        heightAuto: false,
      })
      if (title === messages.newGameCreated.title) {
        dispatch(setState({ modal: undefined }))
      }
    })
    // Only the offerer whose trade was declined receives this; show them a
    // personal toast naming who declined it.
    onGameEvent("trade.declined-notice", ({ declinedByOrder, trade }) => {
      const game = stateRef.current.myGame
      // Keep the declined offer around so opening a new trade with the same
      // player starts from it instead of an empty table.
      if (trade) dispatch(rememberDeclinedTrade(trade))
      const declinerPlayer = game?.situation?.players.find(
        (p) => p.order === declinedByOrder
      )
      const declinerUser = game?.players.find(
        (u) => u.email === declinerPlayer?.id
      )
      const piece = allPieces.find((pc) => pc.id === declinerPlayer?.pieceId)
      playSmallErrorSound()
      dispatch(
        addToastNotification({
          type: "trade-declined",
          username: declinerUser?.username || "A player",
          pieceImgSrc: piece?.imgSrc,
        })
      )
    })
    // Somebody left and a bot took their seat. Goes to the whole room, so skip
    // it for the player who actually left — they are on their way to the lobby
    // and do not need to be told about their own departure.
    onGameEvent(
      "game.player-replaced-by-bot",
      ({ leftPlayerId, playerOrder, username }) => {
        if (leftPlayerId === getUserCreds().email) return
        const game = stateRef.current.myGame
        const player = game?.situation?.players.find(
          (p) => p.order === playerOrder
        )
        const piece = allPieces.find((pc) => pc.id === player?.pieceId)
        // Neutral tap rather than the error sound a declined trade gets — this
        // is news, not a setback, but it still needs to land during a busy turn.
        playLightTap2Sound()
        dispatch(
          addToastNotification({
            type: "player-replaced-by-bot",
            username: username || "A player",
            pieceImgSrc: piece?.imgSrc,
          })
        )
      }
    )
    socket.on("registerOtpExpired", () => {
      dispatch(setState({ showRegisterOtp: false }))
      Swal.fire({
        ...errors.auth.registerOtpExpired,
        icon: "error",
        heightAuto: false,
      })
    })
    socket.on("lobby.sendGameList", (data) => {
      dispatch(setState({ gameList: data.games }))
    })
    socket.on("newgame.getFullDetails", (data) => {
      // A lobby snapshot captured before the game started can arrive after
      // "newgame.gameStarted" (player-detail lookups delay the emit). Never
      // let that stale, situation-less copy clobber a started game's state.
      const currentGame = stateRef.current.myGame
      if (
        currentGame?.id === data.gameDetails.id &&
        currentGame.situation &&
        !data.gameDetails.situation
      ) {
        return
      }
      setGameTimers(data.gameDetails.timers)
      dispatch(setState({ myGame: data.gameDetails }))
    })
    socket.on("newgame.gameStarted", (data) => {
      dispatch(resetStateOnGameStart({}))
      setGameTimers(data.gameDetails.timers)
      dispatch(setState({ myGame: data.gameDetails }))
      dispatch(resetTradeData(undefined as any))
      nav(`/game/${data.gameDetails.id}`)
      dispatch(resetAllModals(undefined as any))
    })

    socket.on("game.updateGlobalState", (data) => {
      dispatch(setState({ ...data }))
    })
    onGameEvent("game.updateGameSituation", (data) => {
      // The clocks first, and outside redux (see utils/gameTimers). Most of
      // these broadcasts are a timer tick and nothing else — once a second, for
      // as long as any clock is running — and routing them through the store
      // gave `myGame` a new identity every second, which re-rendered the board,
      // both sidebars and every open modal to move one number on one ring.
      setGameTimers(data.timers)
      if (data.situation) {
        // The append-only lists (game log, completed/rejected trades) arrive as
        // deltas, so rebuild the full situation against what we already hold.
        const previous = stateRef.current.myGame?.situation
        const situation = mergeSituationUpdate(
          previous,
          data.situation,
          data.appends
        )
        // A gap in one of the lists — take nothing from this update and ask for
        // the whole situation instead, rather than rendering a log with entries
        // missing from the middle.
        if (!situation) {
          requestSituationResync()
          return
        }

        // Trades commit on the backend in one shot, so a situation update is
        // also the only signal that cards changed hands. Detect newly completed
        // trades by the growth of completedTrades and capture the traded cards'
        // current screen positions BEFORE the dispatch re-renders them into
        // their new owners' panels — runTradedCardsFlipAnimation then slides
        // the real cards from these old spots to their new slots. Frontend
        // only: no dummy elements and nothing sent to or from the server.
        // Measured off the merged list rather than the delta so a resend that
        // repeats trades we already have doesn't replay their animations.
        const prevCompleted = previous?.completedTrades?.length || 0
        const newlyCompleted = (situation.completedTrades || []).slice(
          prevCompleted
        )
        const tradedCardRects = new Map<number, DOMRect>()
        newlyCompleted.forEach((trade) => {
          ;[
            ...(trade.givenProperties || []),
            ...(trade.takenProperties || []),
          ].forEach((order) => {
            const el = document.getElementById(`small-card-${order}`)
            if (el) tradedCardRects.set(order, el.getBoundingClientRect())
          })
        })

        // The swap-position card's result arrives as a plain situation update
        // (no piece-movement event). When the pending pair's positions really
        // did exchange, capture the pieces at their PRE-dispatch cells so the
        // crossing-arcs animation can fly clones to the new ones. Frontend
        // only — same precedent as the traded-cards flip above.
        let swapAnim: ReturnType<typeof prepareSwapPositionsAnimation> = null
        const pendingSwap = pendingSwapAnimRef.current
        if (pendingSwap) {
          if (Date.now() > pendingSwap.expiresAt) {
            pendingSwapAnimRef.current = null
          } else {
            const prevPlayers = previous?.players
            const nextPlayers = situation.players as IPlayer[] | undefined
            const prevA = prevPlayers?.find((p) => p.order === pendingSwap.a)
            const prevB = prevPlayers?.find((p) => p.order === pendingSwap.b)
            const nextA = nextPlayers?.find((p) => p.order === pendingSwap.a)
            const nextB = nextPlayers?.find((p) => p.order === pendingSwap.b)
            const swapped =
              prevA &&
              prevB &&
              nextA &&
              nextB &&
              prevA.position !== prevB.position &&
              nextA.position === prevB.position &&
              nextB.position === prevA.position
            if (swapped) {
              pendingSwapAnimRef.current = null
              swapAnim = prepareSwapPositionsAnimation(
                pendingSwap.a,
                pendingSwap.b
              )
              if (swapAnim) {
                dispatch(setState({ pieceMovementAnimationRunning: true }))
              }
            }
          }
        }

        dispatch(
          setState({
            myGame: {
              ...stateRef.current.myGame,
              situation,
            },
          })
        )

        if (tradedCardRects.size) {
          runTradedCardsFlipAnimation(tradedCardRects)
        }

        if (swapAnim) {
          swapAnim.play().finally(() => {
            dispatch(setState({ pieceMovementAnimationRunning: false }))
          })
        }
      }
      // No `else`: a broadcast with no situation on it carries nothing but the
      // clocks, which `setGameTimers` above has already taken. Dispatching
      // anything here is what made a tick cost a full re-render.
    })
    // Settings changed under a running game — the table voted itself onto fast
    // pace. Everything that reads the pace (every countdown ring, every
    // animation duration, the idle clock) goes through myGame.settings, so
    // there is nothing else to do here.
    onGameEvent("game.settings-updated", ({ settings }) => {
      dispatch(setGameSettings(settings))
    })
    // The authoritative situation in full, replacing whatever we hold. Sent in
    // answer to game.requestSituationSync, and by any server flow that needs to
    // overwrite one client's view.
    onGameEvent("game.situationSync", (data) => {
      setGameTimers(data.timers)
      if (!data.situation) return
      dispatch(
        setState({
          myGame: {
            ...stateRef.current.myGame,
            situation: data.situation,
          },
        })
      )
    })
    onGameEvent("game.startPickingRoles", () => {
      dispatch(setModalData({ rolePickingModal: { visible: true } }))
      dispatch(setState({ isTimerPaused: true }))
    })
    onGameEvent("game.announce-winner", (data) => {
      dispatch(removeNotification("bankrupt"))
      // Everything that lists players (both sidebars, the winner card) looks
      // them up by email against myGame.players, so never let a payload without
      // resolved user details replace the ones already loaded — that empties the
      // player list the moment the game is won.
      const hasPlayerDetails = data.players?.every(
        (player) => typeof player === "object" && !!player?.email
      )
      dispatch(
        setState({
          myGame: {
            ...data,
            players: hasPlayerDetails
              ? data.players
              : stateRef.current.myGame?.players || [],
          },
          // Remembered so the board's "Show stats" button can re-open this same
          // screen after it is closed.
          gameEndType: "bankruptcy",
        })
      )
      dispatch(setModalData({ gameEndWithoutStats: { visible: true } }))
    })
    onGameEvent("game.challenge-completed", (data) => {
      // Reflect completion locally so challenge progress updates without a refetch.
      const currentUserData = stateRef.current.userData
      if (
        currentUserData &&
        !currentUserData.completedChallenges?.includes(data.challengeId)
      ) {
        dispatch(
          setState({
            userData: {
              ...currentUserData,
              completedChallenges: [
                ...(currentUserData.completedChallenges || []),
                data.challengeId,
              ],
            },
          })
        )
      }
      dispatch(
        addNotification({
          type: "challenge-completed",
          challengeId: data.challengeId,
          duration: 6000,
        })
      )
    })
    // A corner toast rather than a center banner: this is a heads-up about how
    // much game is left, not something anyone has to answer, so it must never
    // sit over the board while a turn is being played.
    onGameEvent("game.endgame-warning", ({ turnsLeft }) => {
      playLightTap2Sound()
      dispatch(addToastNotification({ type: "endgame-warning", turnsLeft }))
    })
    onGameEvent("game.updateModalData", (data) => {
      dispatch(setModalData({ ...data.modalData }))
    })
    onGameEvent("game.setAnimationData", (data) => {
      dispatch(setAnimationData({ ...data.animationData }))
    })

    socket.on("game.showAlreadyInGameScreen", () => {
      nav("/already-in-game")
    })
    onGameEvent("game.saveDataForMoveCardAnimation", (data) => {
      // const { x, y, top, bottom } = propertyCard?.getBoundingClientRect();
      dispatch(
        setAnimationData({
          propertyMoveAnimations: data.map((transfer) => ({
            propertyOrder: transfer.propertyOrder,
            from: transfer.from,
            to: transfer.to,
            running: false,
          })),
        })
      )

      // Glow the acquired property cards. This is tracked separately from
      // propertyMoveAnimations (which is cleared as soon as the situation
      // updates) so the highlight keeps showing on the real card after the dummy
      // card is swapped out. The glow removes itself from PropertySmallCard once
      // the card has settled into the new owner's panel, so it can't get stuck.
      const acquiredOrders = [
        ...new Set(data.map((transfer) => transfer.propertyOrder)),
      ]
      if (acquiredOrders.length) {
        dispatch(addGlowingProperties(acquiredOrders))
      }
    })
    onGameEvent("game.turn-ended", () => {
      // Trades are turn-independent now, so the trade modal must survive turn
      // changes — only close the turn-bound modals here.
      dispatch(
        setModalData({
          propMng: { visible: false },
          stockExchange: { visible: false },
          bankrupt: { visible: false },
        })
      )
      // An idle countdown belongs to the turn it was raised in. Clearing it
      // here means a badge can't be left stranded on screen if the player who
      // owns it never sends the cancel — because they dropped, or because the
      // server's turn timer ended the turn out from under them.
      dispatch(setIdleWarning(null))
    })

    // Someone else's turn is running out of patience. Only ever sent by the
    // active player's client (the server checks), and never to ourselves.
    onGameEvent("game.idle-warning", ({ playerOrder, warning }) => {
      dispatch(setIdleWarning(warning ? { playerOrder, ...warning } : null))
    })

    // A player dropped and the server is holding their seat for a minute before
    // a bot takes it. Repeats every second while it runs, so this keeps
    // overwriting the same entry; null takes it back off the card.
    onGameEvent("game.bot-takeover-countdown", ({ playerOrder, secondsLeft }) => {
      dispatch(setBotTakeoverCountdown({ playerOrder, secondsLeft }))
    })

    onGameEvent("game.updateStateByEvents", (events) => {
      // Collect all money-transfer animations so we can dispatch them in a single batch
      // (dispatching inside forEach causes stale reads from stateRef)
      const collectedMoneyAnimations: IMoneyTransferAnimation[] = []

      events.forEach((event) => {
        // A card decision (double rent / say no / forced buy), a buy-or-auction
        // choice or the decision hub pauses the game for up to
        // `cardDecisionTime` seconds while a single player picks. Everyone else
        // only sees a board that stopped moving, which reads as a crash — so
        // the server marks both ends of that pause and the board's waiting
        // banner does nothing but follow them.
        if (event.type === "decision-wait-started") {
          dispatch(setState({ pendingDecision: event.decision }))
        }
        if (event.type === "decision-wait-ended") {
          dispatch(setState({ pendingDecision: null }))
        }
        // Somebody put fast pace to the table. The panel itself is drawn from
        // the running tally in the situation (FastPaceVotePanel) — all this
        // does is make sure it is not missed by a player looking at the board.
        // Silence counts as a no, so the opening moment gets a flash across the
        // middle pointing at the corner the vote is answered in; it clears
        // after a couple of seconds and the panel carries the rest.
        if (event.type === "fast-pace-vote-started") {
          playLightTap2Sound()
          dispatch(
            addNotification({
              type: "fast-pace-vote-started",
              playerOrder: event.proposedByOrder,
              duration: 2600,
            })
          )
        }
        // …and how it went. The vote is already cleared from the situation by
        // the time this lands, so the tally comes along with the event.
        if (event.type === "fast-pace-vote-ended") {
          const game = stateRef.current.myGame
          const rejecter = game?.situation?.players.find(
            (p) => p.order === event.rejectedByOrder
          )
          const rejectedByName =
            rejecter &&
            (rejecter.id === getUserCreds().email
              ? "You"
              : game?.players.find((u) => u.email === rejecter.id)?.username ||
                "A player")
          if (event.accepted) playLightTap2Sound()
          else playSmallErrorSound()
          dispatch(
            addToastNotification({
              type: "fast-pace-result",
              accepted: event.accepted,
              acceptedCount: event.acceptedCount,
              rejectedCount: event.rejectedCount,
              totalVoters: event.totalVoters,
              rejectedByName,
              timedOut: event.timedOut,
            })
          )
        }
        if (event.type === "turn-started") {
          const myPlayerOrder = getPlayerOrderFromPlayerId(
            stateRef.current.myGame as IGameDetails,
            getUserCreds().email
          )
          if (event.playerOrder === myPlayerOrder) {
            playTurnStartedSound()
          } else {
            playLightTapSound()
          }
          dispatch(
            addNotification({
              type: "turn-started",
              playerOrder: event.playerOrder,
              duration: 2000,
            })
          )
        }
        if (event.type === "dice-roll-animation-complete") {
          dispatch(setState({ diceRolling: false }))
        }
        if (event.type === "bankrupt") {
          dispatch(
            addNotification({
              type: "bankrupt",
              playerOrder: event.playerOrder,
              creditorOrder: event.credtiorOrder,
              duration: getAnimationTimings(stateRef.current.myGame).bankruptNotification,
            })
          )
        }
        if (event.type === "buy-property-and-end-animation") {
          dispatch(setGameSituation(event.situation))
          dispatch(setAnimationData({ propertyMoveAnimations: undefined }))
        }
        if (event.type === "run-go-to-jail-animation") {
          // This flies the same token the movement animation does, so it counts
          // as a piece movement like any other — cleared in `finally` so a
          // throw inside the timeline (a missing element) can't leave the flag
          // stuck on and the roll button hidden for the rest of the game.
          dispatch(setState({ pieceMovementAnimationRunning: true }))
          runGoToJailAnimation({
            game: stateRef.current.myGame as IGameDetails,
            jailOverlayElm: stateRef.current.elements
              .jailOverlay as HTMLElement,
            playerOrder: event.playerOrder,
          })
            .then(() => {
              socket.emit(
                "game.goToJailAnimationComplete",
                stateRef.current.myGame?.id || ""
              )
            })
            .finally(() => {
              dispatch(setState({ pieceMovementAnimationRunning: false }))
            })
        }

        if (event.type === "keep-action-card") {
          setTimeout(() => {
            // if (!state.actionCardAnimationRunning) {
            dispatch(incementActionCardAnimationRunning(undefined as any))
            // }

            drawAndKeepCard(
              stateRef.current.elements,
              event.cardId,
              "chance",
              event.playerOrder
            )
            setTimeout(() => {
              // if (state.actionCardAnimationRunning) {
              dispatch(decrementActionCardAnimationRunning(undefined as any))
              // }
            }, getAnimationTimings(stateRef.current.myGame).drawAndKeepCard)
          }, event.delay || 0)
        }

        if (event.type === "property-changes-applied") {
          dispatch(
            setAnimationData({
              propChangesAppliedAnimation: event.data,
            })
          )
          const activePlayer = stateRef.current.myGame!.situation!.players.find(
            (player) => player.id === stateRef.current.myGame!.situation!.turn
          )
          setTimeout(() => {
            const moneyAnimation: IMoneyTransferAnimation = {
              from: event.netMoneyChange > 0 ? bankOrder : activePlayer!.order,
              to: event.netMoneyChange > 0 ? activePlayer!.order : bankOrder,
              started: false,
              money: Math.abs(event.netMoneyChange),
              type: "money-transfer",
              id: getRandomId(),
            }
            if (event.netMoneyChange) {
              // Append so an already-running animation keeps playing instead of
              // being wiped out by replacing the whole array.
              dispatch(addMoneyAnimation(moneyAnimation))
            }
            dispatch(
              setAnimationData({
                propChangesAppliedAnimation: {
                  houseAnimations: [],
                  mortgageAnimations: [],
                },
              })
            )
          }, event.totalDuration)
        }

        if (event.type === "force-downgrade-highlight") {
          dispatch(
            setState({ forceDowngradeHighlightOrder: event.propertyOrder })
          )
          setTimeout(() => {
            dispatch(setState({ forceDowngradeHighlightOrder: null }))
          }, 2500)
        }

        if (event.type === "show-double-rent-modal") {
          const thisPlayer = getCurrentPlayer(
            stateRef.current.myGame as IGameDetails
          )
          if (thisPlayer?.order === event.ownerOrder) {
            dispatch(
              setModalData({
                doubleRent: {
                  visible: true,
                  ownerOrder: event.ownerOrder,
                  playerOrder: event.playerOrder,
                  propertyId: event.propertyId,
                  rent: event.rent,
                },
              })
            )
          }
        }
        if (event.type === "show-decision-hub") {
          if (
            getUserCreds().email === stateRef.current.myGame?.situation?.turn
          ) {
            dispatch(
              setModalData({
                decisionHub: {
                  visible: true,
                },
              })
            )
          }
        }
        if (event.type === "show-say-no-modal") {
          const thisPlayer = getCurrentPlayer(
            stateRef.current.myGame as IGameDetails
          )
          if (thisPlayer?.order === event.playerOrder) {
            dispatch(
              setModalData({
                sayNo: {
                  visible: true,
                  rent: event.rent,
                  ownerOrder: event.ownerOrder,
                  playerOrder: event.playerOrder,
                  propertyId: event.propertyId,
                },
              })
            )
          }
        }
        if (event.type === "show-forced-buy-modal") {
          const thisPlayer = getCurrentPlayer(
            stateRef.current.myGame as IGameDetails
          )
          if (thisPlayer?.order === event.playerOrder) {
            dispatch(
              setModalData({
                forcedBuy: {
                  visible: true,
                  playerOrder: event.playerOrder,
                  propertyOrder: event.propertyOrder,
                },
              })
            )
          }
        }

        if (event.type === "money-transfer") {
          if (event.money === 0) return
          collectedMoneyAnimations.push({
            id: getRandomId(),
            money: event.money,
            type: "money-transfer",
            from: event.from,
            to: event.to,
            started: false,
            reason: event.reason,
          } as IMoneyTransferAnimation)
        }
        if (event.type === "hide-all-action-card-modals") {
          dispatch(
            setModalData({
              buyOrAuction: { visible: false },
              doubleRent: { visible: false },
              sayNo: { visible: false },
              forcedBuy: { visible: false },
              decisionHub: { visible: false },
            })
          )
        }
        if (event.type === "private-buy") {
          dispatch(setModalData({ buyOrAuction: { visible: false } }))
          dispatch(
            addNotification({
              type: "private-buy",
              propertyOrder: event.propertyOrder,
              playerOrder: event.playerOrder,
              duration: getAnimationTimings(stateRef.current.myGame).privateBuyNotification,
            })
          )
          const myPlayerOrder = getPlayerOrderFromPlayerId(
            stateRef.current.myGame as IGameDetails,
            getUserCreds().email
          )
          if (event.playerOrder === myPlayerOrder) {
            playAuctionWonSound()
          } else {
            playLightTap2Sound()
          }
        }
        if (event.type === "piece-movement") {
          const movementTypeToFunctionObject = {
            jump: startPieceMovementAnimation,
            "no-jump": startPieceMovementAnimationWithoutJumping,
          } as const
          dispatch(setState({ pieceMovementAnimationRunning: true }))
          const movementFunctionToCall =
            movementTypeToFunctionObject[event.movementType || "jump"]
          const cellsToMoveUpon = getInBetweenCellsIdsForMovement(
            event.from,
            event.to,
            stateRef.current.myGame?.settings?.mapId || "0",
            event.isBackward
          )
          const totalTimeTaken =
            event.movementType === "jump"
              ? getAnimationTimings(stateRef.current.myGame).pieceMovementJump *
                (cellsToMoveUpon.length - 1)
              : getAnimationTimings(stateRef.current.myGame).pieceMovementWithoutJump

          setTimeout(() => {
            const movedPlayerId =
              stateRef.current.myGame?.situation?.players.find(
                (player) => player.order === event.playerOrder
              )?.id
            dispatch(setState({ pieceMovementAnimationRunning: false }))
            if (movedPlayerId === getUserCreds().email)
              socket.emit(
                "game.movementAnimationComplete",
                stateRef.current.myGame?.id || "",
                testChanceId,
                testCommunityChestId
              )
          }, totalTimeTaken)
          movementFunctionToCall(
            event,
            stateRef.current.myGame as IGameDetails
          ).then()
        }
        if (event.type === "show-auction-bids") {
          dispatch(
            setState({ showAllAuctionBids: true, auctionBids: event.auctions })
          )
        }
        if (event.type === "update-game-timers") {
          setGameTimers(event.timers)
        }
        if (event.type === "auction-tied") {
          dispatch(setState({ showAllAuctionBids: false }))
          dispatch(
            addNotification({
              duration: 2000,
              type: "auction-tied",
              propertyOrder: event.propertyOrder,
            })
          )
          // A quick game skips the auction results modal entirely — the banner
          // is the whole story, and the sidebar button is still there for
          // anyone who wants the bid-by-bid breakdown.
          if (!stateRef.current.myGame?.settings?.isQuickGame) {
            setTimeout(() => {
              dispatch(
                setModalData({
                  auctionResult: {
                    visible: true,
                    propertyOrder: event.propertyOrder,
                  },
                })
              )

              setTimeout(() => {
                dispatch(setModalData({ auctionResult: { visible: false } }))
              }, getAnimationTimings(stateRef.current.myGame).auctionResultNotification)
            }, 2000)
          }
        }
        if (event.type === "auction-won-animation") {
          dispatch(setState({ showAllAuctionBids: false }))
          dispatch(
            addNotification({
              duration: 2000,
              type: "auction-won",
              playerOrder: event.playerOrder,
              propertyOrder: event.propertyOrder,
              amount: event.amount,
            })
          )
          const myPlayerOrder = getPlayerOrderFromPlayerId(
            stateRef.current.myGame as IGameDetails,
            getUserCreds().email
          )

          if (event.playerOrder === myPlayerOrder) {
            runAuctionWonAnimation()
            playAuctionWonSound()
          } else {
            playLightTap2Sound()
          }
          if (!stateRef.current.myGame?.settings?.isQuickGame) {
            setTimeout(() => {
              dispatch(
                setModalData({
                  auctionResult: {
                    visible: true,
                    propertyOrder: event.propertyOrder,
                  },
                })
              )

              setTimeout(() => {
                dispatch(setModalData({ auctionResult: { visible: false } }))
              }, getAnimationTimings(stateRef.current.myGame).auctionResultNotification)
            }, 4000)
          }
        }
        if (event.type === "play-action-card") {
          dispatch(incementActionCardAnimationRunning(undefined as any))
          setTimeout(() => {
            dispatch(decrementActionCardAnimationRunning(undefined as any))
          }, getAnimationTimings(stateRef.current.myGame).playActionCard)
          playActionCard(
            stateRef.current.elements,
            event.cardId,
            event.cardType || "chance",
            event.playerOrder as PlayerOrder
          )
          // Targeted cards release a striking beam from the card once it has
          // settled at the center of the screen. Cards that hit everyone (e.g.
          // "get 10% from all players") fan a staggered beam out to every other
          // player; property-aimed cards (forced auction/buy/downgrade,
          // purchase-at-double-price) strike the board cell; otherwise cards
          // aimed at one player (tax audit, property tax, house tax, …) strike
          // that player's panel.
          const targetProperty = event.targetedPropertyOrder
          const targetPlayer = event.targetedPlayerOrder
          const beamDelay = getAnimationMultiplier(stateRef.current.myGame) * 850
          const isSwapCard =
            event.cardId === "swap-position-with-player" &&
            targetPlayer != null &&
            targetPlayer !== event.playerOrder
          if (isSwapCard) {
            // The swap card strikes the two TILES about to exchange occupants
            // (both pieces' current cells, captured pre-swap), not the target
            // player's panel — the second bolt stays silent so the pair of
            // zaps doesn't stack.
            const players = stateRef.current.myGame?.situation?.players || []
            const swapTiles = [
              ...new Set(
                [event.playerOrder, targetPlayer]
                  .map(
                    (order) => players.find((p) => p.order === order)?.position
                  )
                  .filter((pos): pos is number => pos != null)
              ),
            ]
            swapTiles.forEach((pos, i) => {
              setTimeout(() => {
                runStrikingBeamAnimation({
                  toPropertyOrder: pos,
                  playSound: i === 0,
                })
              }, beamDelay + i * 120 * getAnimationMultiplier(stateRef.current.myGame))
            })
          } else if (allPlayersBeamCards.includes(event.cardId)) {
            const otherPlayers = (
              stateRef.current.myGame?.situation?.players || []
            ).filter((p) => p.order !== event.playerOrder && !p.isBankrupt)
            otherPlayers.forEach((p, i) => {
              setTimeout(() => {
                // Only the first beam plays the strike sound so the fan-out
                // doesn't stack a wall of overlapping zaps.
                runStrikingBeamAnimation({
                  toOrder: p.order,
                  playSound: i === 0,
                })
              }, beamDelay + i * 120 * getAnimationMultiplier(stateRef.current.myGame))
            })
          } else if (targetProperty != null) {
            setTimeout(() => {
              runStrikingBeamAnimation({ toPropertyOrder: targetProperty })
            }, beamDelay)
          } else if (
            targetPlayer != null &&
            targetPlayer !== event.playerOrder
          ) {
            setTimeout(() => {
              runStrikingBeamAnimation({ toOrder: targetPlayer as PlayerOrder })
            }, beamDelay)
          }
          // Arm the swap animation: the exchanged positions land in a later
          // situation update (~drawChanceCard/2 after this event — right as
          // the striking beams hit the tiles), where the updateGameSituation
          // handler picks this up.
          if (isSwapCard) {
            pendingSwapAnimRef.current = {
              a: event.playerOrder as PlayerOrder,
              b: targetPlayer as PlayerOrder,
              expiresAt: Date.now() + getAnimationTimings(stateRef.current.myGame).drawChanceCard + 8000,
            }
          }
        }
        if (event.type === "show-stock-exchange") {
          if (stateRef.current.myGame?.situation?.turn === getUserCreds().email)
            dispatch(setModalData({ stockExchange: { visible: true } }))
        }
        if (event.type === "start-auction") {
          if (
            stateRef.current.myGame?.situation?.turn === getUserCreds().email
          ) {
            socket.emit(
              "auction.started",
              stateRef.current.myGame?.id || "",
              event.propertyOrder
            )
          }
        }
        if (event.type === "show-buy-or-auction-modal") {
          if (event && event.playerId === getUserCreds().email) {
            dispatch(
              setModalData({
                buyOrAuction: {
                  visible: true,
                  propertyId: event.propertyOrder,
                },
              })
            )
          }
        }
        if (event.type === "hide-trade-screen") {
          dispatch(setModalData({ trade: { visible: false } }))
        }
        if (event.type === "trade" && event.status === "offered") {
          // Only chime for the player the trade was offered to.
          const myPlayerOrder = getPlayerOrderFromPlayerId(
            stateRef.current.myGame as IGameDetails,
            getUserCreds().email
          )
          if (event.trade?.offeredPlayerOrder === myPlayerOrder) {
            playTradeOfferedSound()
          }
        }
        if (event.type === "trade" && event.status === "accepted") {
          // Successful trades are announced to everyone via a toast showing the
          // full exchange (both players, properties and money).
          const t = event.trade
          dispatch(
            addToastNotification({
              type: "trade-completed",
              tradeId: t.id,
              offeringPlayerOrder: t.offeringPlayerOrder,
              offeredPlayerOrder: t.offeredPlayerOrder,
              givenProperties: t.givenProperties || [],
              takenProperties: t.takenProperties || [],
              moneyGiven: t.moneyGiven || 0,
              moneyTaken: t.moneyTaken || 0,
            })
          )
          // Trades apply instantly (no move-card animation) — glow the traded
          // cards in place in their new owners' panels instead.
          const tradedOrders = [
            ...new Set([
              ...(t.givenProperties || []),
              ...(t.takenProperties || []),
            ]),
          ]
          if (tradedOrders.length) {
            dispatch(addTradeGlowProperties(tradedOrders))
          }
          // A completed trade with someone makes any offer they previously
          // declined stale — don't hand it back as a draft next time.
          const myOrder = getPlayerOrderFromPlayerId(
            stateRef.current.myGame as IGameDetails,
            getUserCreds().email
          )
          if (myOrder === t.offeringPlayerOrder) {
            dispatch(forgetDeclinedTrade(t.offeredPlayerOrder))
          } else if (myOrder === t.offeredPlayerOrder) {
            dispatch(forgetDeclinedTrade(t.offeringPlayerOrder))
          }
        }
      })

      // "Collect from all players" cards (specifically "get 10% from all
      // players") emit one action-card transfer per payer, all landing on the
      // active player within this single batch. Tag those with a shared
      // labelGroupId so the receiver shows one combined flyoff label with the
      // total, instead of a stack of small per-payer labels. The flying bills
      // still animate individually from each payer.
      const actionCardGainsByReceiver = new Map<PlayerOrder, number>()
      collectedMoneyAnimations.forEach((a) => {
        if (a.reason === "action-card") {
          actionCardGainsByReceiver.set(
            a.to,
            (actionCardGainsByReceiver.get(a.to) || 0) + 1
          )
        }
      })
      const labelGroupIdByReceiver = new Map<PlayerOrder, string>()
      collectedMoneyAnimations.forEach((a) => {
        if (
          a.reason === "action-card" &&
          (actionCardGainsByReceiver.get(a.to) || 0) >= 2
        ) {
          if (!labelGroupIdByReceiver.has(a.to)) {
            labelGroupIdByReceiver.set(a.to, getRandomId())
          }
          a.labelGroupId = labelGroupIdByReceiver.get(a.to)
        }
      })

      // Append all collected money-transfer animations in a single batch. Using
      // the reducer (which pushes onto the live state) instead of setState means
      // any animations already running are preserved rather than replaced.
      if (collectedMoneyAnimations.length > 0) {
        dispatch(addMoneyAnimations(collectedMoneyAnimations))
      }
    })

    onGameEvent("game.announce-winner-and-show-stats", () => {
      dispatch(setState({ gameEndType: "standings" }))
      dispatch(setModalData({ endGame: { visible: true } }))
    })

    onGameEvent("game.allRolesPicked", () => {
      dispatch(setModalData({ rolePickingModal: { visible: false } }))
      dispatch(
        setState({
          timerValue: getGameTimings(stateRef.current.myGame).turnTotalTime,
          isTimerPaused: false,
        })
      )
    })

    socket.on("lobby.show-invitation", (data) => {
      // Show invitation toast notification
      dispatch(
        addToastNotification({
          type: "game-invite",
          gameId: data.gameId,
          invitedBy: data.invitedBy,
          gameTitle: data.gameTitle,
          currentPlayers: data.currentPlayers,
          maxPlayers: data.maxPlayers,
        })
      )
    })

    socket.on("playerCounts", (data) => {
      dispatch(setState({ playerCounts: data }))
    })

    socket.on("chat.receive-room-message", (data) => {
      if (!featureFlags.showChat) return
      // Store the chat message in global state
      dispatch(
        addChatMessage({
          roomid: data.roomid,
          message: {
            username: data.username,
            message: data.message,
            timestamp: data.timestamp,
          },
        })
      )

      // Show chat cloud for the player who sent the message
      if (stateRef.current.myGame?.situation?.players) {
        // Find the player by username
        // First try to find by username in the game's players array
        const player = stateRef.current.myGame.players.find(
          (user) => user.username === data.username
        )

        if (player) {
          // Find the corresponding player in the situation
          const situationPlayer =
            stateRef.current.myGame.situation.players.find(
              (p) => p.id === player.email
            )

          if (situationPlayer) {
            dispatch(
              showChatCloud({
                playerOrder: situationPlayer.order,
                message: data.message,
                username: data.username,
              })
            )

            // Hide the chat cloud after 5 seconds
            setTimeout(() => {
              dispatch(
                hideChatCloud({
                  playerOrder: situationPlayer.order,
                })
              )
            }, 4000)
          }
        }
      }
    })

    return () => {
      // Every event registered in this effect MUST be removed here. The deps
      // include myGame, which gets a new reference on every server update
      // (timer ticks arrive each second), so any name missing from this list
      // leaks one listener per re-run and property/modal events end up
      // dispatching hundreds of times per emit as a game progresses.
      socket.off("game.announce-winner-and-show-stats")
      socket.off("error")
      socket.off("message")
      socket.off("trade.declined-notice")
      socket.off("game.player-replaced-by-bot")
      socket.off("registerOtpExpired")
      socket.off("game.updateGameSituation")
      socket.off("game.situationSync")
      socket.off("game.settings-updated")
      socket.off("game.updateStateByEvents")
      socket.off("game.announce-winner")
      socket.off("lobby.show-invitation")
      socket.off("chat.receive-room-message")
      socket.off("playerCounts")
      socket.off("game.challenge-completed")
      socket.off("game.endgame-warning")
      socket.off("lobby.sendGameList")
      socket.off("newgame.getFullDetails")
      socket.off("newgame.gameStarted")
      socket.off("game.updateGlobalState")
      socket.off("game.startPickingRoles")
      socket.off("game.updateModalData")
      socket.off("game.setAnimationData")
      socket.off("game.showAlreadyInGameScreen")
      socket.off("game.saveDataForMoveCardAnimation")
      socket.off("game.turn-ended")
      socket.off("game.idle-warning")
      socket.off("game.bot-takeover-countdown")
      socket.off("game.allRolesPicked")
    }
  }, [
    dispatch,
    // animationData.propertyMoveAnimations,
    myGame,
    myGameId,
    elements.jailOverlay,
    testChanceId,
    testCommunityChestId,
    actionCardAnimationRunning,
    // this is a hack to prevent the animation from running when the component is unmounted
    elements,
    elements.chanceCards,
    elements.communityChestCards,
  ])
  useEffect(() => {
    if (myGameTurn === getUserCreds().email) {
      dispatch(setState({ disableEndTurnLocally: false }))
    }
  }, [myGameTurn, dispatch])
  // No decision outlives the turn it was asked in, so a new turn always wipes
  // the waiting banner — a second guard against it ever getting stuck.
  useEffect(() => {
    dispatch(setState({ pendingDecision: null }))
  }, [myGameTurn, dispatch])
  // useEffect(() => {
  //    const propertyMoveAnimationEndEvent =
  //       state.myGame?.situation?.events.find(
  //          (event) => event.type === "property-move-animation-end"
  //       );
  //    if (propertyMoveAnimationEndEvent) {
  //       dispatch({ setAnimationData: { propertyMoveAnimations: undefined } });
  //    }
  // }, [state.myGame?.situation?.events]);
  useEffect(() => {
    const showBuyOrAuctionModalEvent = myGameSituationEvents?.find(
      (event) => event.type === "show-buy-or-auction-modal"
    ) as IShowBuyOrAuctionModal

    const activePlayer = myGameSituation?.players.find(
      (p) => p.id === myGameSituation?.turn
    )
    // const moneyTransferEvent = myGameSituation?.events.find(
    //    (event) => event.type ===
    // );

    if (
      showBuyOrAuctionModalEvent &&
      showBuyOrAuctionModalEvent.playerId === getUserCreds().email
    ) {
      dispatch(
        setModalData({
          buyOrAuction: {
            visible: true,
            propertyId: showBuyOrAuctionModalEvent.propertyOrder,
          },
        })
      )
    }
  }, [myGameSituationEvents, myGameSituation, dispatch])

  const myMap = mapDesigns.find((map) => map.id === myGame?.settings.mapId)

  const chanceOptions = myMap?.allChances?.map((card) => ({
    value: card.id,
    label: card.text,
  }))
  const [testChanceOption, setTestChanceOption] = useState<{
    value: ChanceId
    label: string
  }>()
  useEffect(() => {
    if (!myGameSituation) return
    if (declaredBankruptcy) return

    // Hide bankrupt modal while trade flow is active; it will re-appear when trade closes
    const tradeFlowActive =
      modalData.trade?.visible || tempTradeData?.status === "choosing-player"
    if (tradeFlowActive) {
      if (modalData.bankrupt?.visible) {
        dispatch(setModalData({ bankrupt: { visible: false } }))
      }
      return
    }

    const currentPlayer = myGameSituation.players.find(
      (player) => player.id === getUserCreds().email
    ) as IPlayer

    if (currentPlayer?.money < 0) {
      if (!modalData?.bankrupt?.visible) {
        dispatch(setModalData({ bankrupt: { visible: true } }))
      }
    } else if (modalData.bankrupt?.visible) {
      dispatch(setModalData({ bankrupt: { visible: false } }))
    }
  }, [
    modalData.bankrupt,
    modalData.trade,
    tempTradeData?.status,
    myGameSituation,
    declaredBankruptcy,
    dispatch,
  ])

  // const [testCommunityChestId, setTestCommunityChestId] = useState<CommunityChestId>("advance-to-go-collect-200");
  //
  // useEffect(() => {
  //   socket.on("disconnect", () => {
  //     dispatch({ setState: {  } })
  //   })

  // }, [])
  useEffect(() => {
    if (!socket || !myGame) return

    const handleGameEvents = (events: IStateChangeEventFromSeverToClient[]) => {
      events.forEach((event) => {
        switch (event.type) {
          case "draw-card":
            // if (event.keep) {
            //   drawAndKeepCard(
            //     elements,
            //     event.cardId,
            //     event.cardType,
            //     event.playerOrder as PlayerOrder
            //   )
            // } else {
            // Counted like the other card animations (keep-action-card,
            // play-action-card) so everything that has to stand aside while a
            // card is on screen — the clock tick, the stock modal — knows this
            // one is running too.
            if (drawCard(elements, event.cardId, event.cardType)) {
              dispatch(incementActionCardAnimationRunning(undefined as any))
              setTimeout(() => {
                dispatch(decrementActionCardAnimationRunning(undefined as any))
              }, getAnimationTimings(stateRef.current.myGame).drawChanceCard)
            }
            // }
            break
        }
      })
    }

    socket.on("game.updateStateByEvents", handleGameEvents)

    return () => {
      socket.off("game.updateStateByEvents", handleGameEvents)
    }
  }, [socket, myGame, elements])

  useEffect(() => {
    const handleOrientationChange = () => {
      const isPortrait = window.matchMedia("(orientation: portrait)").matches
      dispatch(setOrientation(isPortrait))
      // Seeded once at module load, so without this a window that merely
      // started narrow stayed "mobile" for the rest of the session.
      dispatch(setIsMobile(window.innerWidth < 1000))
    }

    // Initial check
    handleOrientationChange()

    // Add event listeners
    window.addEventListener("orientationchange", handleOrientationChange)
    window.addEventListener("resize", handleOrientationChange)

    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange)
      window.removeEventListener("resize", handleOrientationChange)
    }
  }, [dispatch])

  // Add cleanup effect for audio manager
  useEffect(() => {
    return () => {
      // Clean up audio objects when component unmounts
      audioManager.cleanup()
    }
  }, [])
  if (!imagesLoaded) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-white text-xl font-semibold mb-2">
            Loading Assets...
          </h2>
          <p className="text-gray-300 text-sm">
            Please wait while we load all game assets
          </p>
        </div>
        <LandscapeOverlay />
      </div>
    )
  }

  return (
    <>
      <div
        className={classNames(
          "flex flex-col fixed bottom-5 right-5 z-10 w-80",
          { hidden: cheats === false }
        )}
      >
        <select
          className="w-full"
          data-testid="test.chance-id"
          //@ts-ignore
          onChange={(e) => setTestChanceId(e.target.value as ChanceId)}
        >
          <option value={""}>Random</option>
          {myMap?.allChances.map((chance) => (
            <option value={chance.id}>{chance.text}</option>
          ))}
        </select>
        <select
          className="w-full"
          data-testid="test.community-chest-id"
          //@ts-ignore
          onChange={(e) =>
            setTestCommunityChestId(e.target.value as EventCardId)
          }
        >
          <option value={""}>Random</option>
          {myMap?.allCommunityChests.map((chance) => (
            <option value={chance.id}>{chance.text}</option>
          ))}
        </select>
        <button
          className="bg-blue-500 text-white p-2 rounded-md"
          onClick={() => socket?.emit("trailer.action-1", myGameId || "")}
        >
          Trailer Action 1
        </button>
        <button
          className="bg-blue-500 text-white p-2 rounded-md"
          onClick={() => socket?.emit("trailer.action-2", myGameId || "")}
        >
          Trailer Action 2
        </button>
      </div>

      {modalData.jail?.visible && <JailModalPage></JailModalPage>}
      <GameEndModalWithoutStats />

      <Routes>
        <Route path="/l2" element={<LandingPage />} />
        <Route
          path="/"
          element={
            JSON.parse(localStorage.getItem("user") || "{}")?.token ? (
              <Navigate to="/lobby" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/login" element={<LoginPage></LoginPage>}></Route>
        <Route path="/register" element={<RegisterPage></RegisterPage>}></Route>
        <Route element={<PrivateRoute></PrivateRoute>}>
          <Route path="/lobby" element={<LobbyPage></LobbyPage>}></Route>
        </Route>
        <Route element={<PrivateRoute></PrivateRoute>}>
          <Route path="/challenges-list" element={<ChallengesList />}></Route>
        </Route>
        <Route element={<PrivateRoute></PrivateRoute>}>
          <Route
            path="/newgame/:id"
            element={<NewGamePage></NewGamePage>}
          ></Route>
        </Route>
        <Route element={<PrivateRoute></PrivateRoute>}>
          <Route path="/game/:id" element={<GamePage></GamePage>}></Route>
        </Route>

        <Route
          path="/already-in-game"
          element={<AlreadyInGamePage></AlreadyInGamePage>}
        ></Route>
        <Route
          path="/forgot-pass"
          element={<ForgotPasswordPage></ForgotPasswordPage>}
        ></Route>
        <Route
          path="/forgot-pass-otp"
          element={<ForgotPasswordOtpPage></ForgotPasswordOtpPage>}
        ></Route>
        <Route
          path="/set-new-password"
          element={<SetNewPasswordPage></SetNewPasswordPage>}
        ></Route>
        <Route element={<PrivateRoute></PrivateRoute>}>
          <Route path="/profile-settings" element={<ProfileSettings />} />
        </Route>

        <Route path="/stats" element={<Stats />} />
        <Route path="/game-history" element={<GameHistory />} />
        <Route path="/game-history/:gameid" element={<GameHistoryDetail />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/trade-simulator" element={<TradeSimulator />} />
        <Route path="/how-to-play" element={<HowToPlay />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* The URL given to Google Play as the account-deletion link. Public on
            purpose: somebody locked out of their account still has to reach it. */}
        <Route path="/delete-account" element={<DeleteAccount />} />
      </Routes>

      <ToastContainer />
      {/* Above the router, not inside a route: the game is not the only screen
          that wants the long edge — the lobby, trade and stats screens are laid
          out the same way. */}
      <LandscapeOverlay />
    </>
  )
}

export default App
